import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { ChatHistoryItem, ChatRequest, NEW_CONVERSATION_PLACEHOLDER } from '../Interface';

interface InterruptedMessage {
  text: string;
  conversationId?: number;
}

interface UseConversationStateProps {
  chatHistory: ChatHistoryItem;
  selectedConversationId?: number | null;
  index: number;
  streamingAbortControllerRef: React.MutableRefObject<AbortController | null>;
  setInterruptedMessage: (msg: InterruptedMessage | null) => void;
}

interface UseConversationStateReturn {
  activeConversation: ChatHistoryItem;
  localConversation: ChatHistoryItem | null;
  setLocalConversation: (conv: ChatHistoryItem | null) => void;
  chatHistoryRef: React.MutableRefObject<ChatHistoryItem>;
  updateConversation: (updates: Partial<ChatHistoryItem>) => ChatHistoryItem;
  getBaseConversation: () => { base: ChatHistoryItem; requests: ChatRequest[] };
}

export const useConversationState = ({
  chatHistory,
  selectedConversationId,
  index,
  streamingAbortControllerRef,
  setInterruptedMessage,
}: UseConversationStateProps): UseConversationStateReturn => {
  const safeChatHistory: ChatHistoryItem = chatHistory || NEW_CONVERSATION_PLACEHOLDER;
  const [localConversation, setLocalConversation] = useState<ChatHistoryItem | null>(null);
  const chatHistoryRef = useRef(safeChatHistory);
  const previousConversationIdRef = useRef<number | null | undefined>(selectedConversationId);
  const interruptedMessageRef = useRef<InterruptedMessage | null>(null);

  // Determine active conversation
  const activeConversation = useMemo(() => {
    const isNewConversationSlot = index === 0 && selectedConversationId === null;

    // Use local conversation if it matches current selection
    if (localConversation) {
      const matchesCurrent =
        localConversation.conversationId === selectedConversationId ||
        (localConversation.conversationId === undefined && isNewConversationSlot);
      if (matchesCurrent) return localConversation;
    }

    // For new conversation slot, use local if available
    if (isNewConversationSlot && localConversation?.requests?.length) {
      return localConversation;
    }

    // Filter incomplete requests for display
    const requests = safeChatHistory.requests || [];
    const cleanedRequests = requests.filter((req) => req.response !== '');
    return cleanedRequests.length !== requests.length
      ? { ...safeChatHistory, requests: cleanedRequests }
      : safeChatHistory;
  }, [localConversation, selectedConversationId, safeChatHistory, index]);

  // Handle conversation switching
  useEffect(() => {
    const isStreaming = streamingAbortControllerRef.current !== null;
    const previousId = previousConversationIdRef.current;
    const isNewConversationSlot = selectedConversationId === null && index === 0;

    // Detect if new conversation just got its ID (not a user switch)
    const isGettingNewId =
      (previousId === null || previousId === undefined) &&
      selectedConversationId !== null &&
      localConversation?.conversationId === selectedConversationId;

    const didSwitch = previousId !== selectedConversationId && !isGettingNewId;

    // Abort streaming on user-initiated switch
    if (didSwitch && streamingAbortControllerRef.current) {
      streamingAbortControllerRef.current.abort();
      streamingAbortControllerRef.current = null;

      const current = chatHistoryRef.current;
      const lastRequest = current.requests?.[current.requests.length - 1];
      // Clean up if last request has no response or only partial response (streaming was interrupted)
      // A completed response will have responseCreatedAt set (only set in onComplete)
      // So if response exists but responseCreatedAt is missing, it's a partial/incomplete response
      const isIncomplete =
        lastRequest && (!lastRequest.response || lastRequest.response === '' || !lastRequest.responseCreatedAt);

      if (isIncomplete) {
        // Remove the incomplete/partial request
        const cleanedRequests = current.requests.filter(
          (req) =>
            !(req.text === lastRequest.text && req.createdAt === lastRequest.createdAt && !req.responseCreatedAt),
        );
        setLocalConversation({ ...current, requests: cleanedRequests });
        chatHistoryRef.current = { ...current, requests: cleanedRequests };
        // Only allow retry for existing conversations (with an ID)
        if (current.conversationId !== undefined) {
          const interruptedMsg = { text: lastRequest.text, conversationId: current.conversationId };
          interruptedMessageRef.current = interruptedMsg;
          setInterruptedMessage(interruptedMsg);
        }
      }
    }

    // Clear interrupted message when switching to a different conversation
    // This prevents the retry banner from briefly showing on the wrong conversation
    if (didSwitch) {
      const currentInterrupted = interruptedMessageRef.current;
      // Clear interrupted message if it doesn't belong to the newly selected conversation
      if (currentInterrupted && currentInterrupted.conversationId !== selectedConversationId) {
        interruptedMessageRef.current = null;
        setInterruptedMessage(null);
      }
    }

    // Clear local state on switch (but not while streaming)
    if (!isStreaming) {
      const shouldClear =
        (isNewConversationSlot && localConversation?.conversationId !== undefined) ||
        (didSwitch && localConversation?.conversationId !== selectedConversationId);
      if (shouldClear) setLocalConversation(null);
    }

    previousConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId, index, safeChatHistory, setInterruptedMessage]);

  // Sync ref with active conversation
  useEffect(() => {
    chatHistoryRef.current = activeConversation;
  }, [activeConversation]);

  // Helper to update conversation state
  const updateConversation = useCallback((updates: Partial<ChatHistoryItem>): ChatHistoryItem => {
    const current = chatHistoryRef.current;
    const updated = { ...current, ...updates };
    setLocalConversation(updated);
    chatHistoryRef.current = updated;
    return updated;
  }, []);

  // Get base conversation for new message
  const getBaseConversation = useCallback((): { base: ChatHistoryItem; requests: ChatRequest[] } => {
    const currentId = selectedConversationId || undefined;
    const isNew = !currentId;

    // New conversation: prefer local draft, fallback to empty placeholder
    if (isNew) {
      if (localConversation && !localConversation.conversationId) {
        return { base: localConversation, requests: [...(localConversation.requests || [])] };
      }
      return { base: NEW_CONVERSATION_PLACEHOLDER, requests: [] };
    }

    // Existing conversation: prefer local state, then chatHistory, fallback to placeholder with ID
    if (localConversation?.conversationId === currentId) {
      return { base: localConversation, requests: [...(localConversation.requests || [])] };
    }
    if (safeChatHistory.conversationId === currentId) {
      return { base: safeChatHistory, requests: [...(safeChatHistory.requests || [])] };
    }
    return { base: { ...NEW_CONVERSATION_PLACEHOLDER, conversationId: currentId, kind: 'conversation' }, requests: [] };
  }, [selectedConversationId, localConversation, safeChatHistory]);

  return {
    activeConversation,
    localConversation,
    setLocalConversation,
    chatHistoryRef,
    updateConversation,
    getBaseConversation,
  };
};
